import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { businessInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about-us' },
    { label: 'Industries', href: '/industries' },
    { label: 'Contact', href: '/contact' },
  ];

  const serviceLinks = [
    { label: 'Security', href: '/what-we-do/security', description: 'CCTV, alarm systems, access control and more' },
    { label: 'Electrical', href: '/what-we-do/electrical', description: 'EV chargers, light & power, switchboard upgrades' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/85 backdrop-blur-xl border-b border-gray-200/50 py-3 shadow-lg shadow-black/5'
          : 'bg-white/75 backdrop-blur-lg py-5'
      }`}
    >
      <div className="container mx-auto px-6 flex items-center justify-between">
        {/* Logo - Text Only */}
        <a href="/" className="flex items-center group">
          <img 
            src="https://customer-assets.emergentagent.com/job_5bafb83c-d66a-46c6-a3cc-c3c946be451b/artifacts/d5sefqmp_Securelec%20logo.png" 
            alt="Securelec" 
            className="h-10"
          />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.slice(0, 2).map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-securelec-gray hover:text-securelec-red transition-colors duration-300 text-sm font-medium tracking-wide uppercase"
            >
              {link.label}
            </a>
          ))}
          
          {/* Services Dropdown */}
          <NavigationMenu className="z-50">
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger 
                  className="!bg-transparent hover:!bg-transparent focus:!bg-transparent data-[state=open]:!bg-transparent text-securelec-gray hover:text-securelec-red data-[state=open]:text-securelec-red transition-colors duration-300 text-sm font-medium tracking-wide uppercase h-auto p-0 px-0"
                  data-testid="services-dropdown-trigger"
                >
                  Services
                </NavigationMenuTrigger>
                <NavigationMenuContent className="bg-white/95 backdrop-blur-xl border border-gray-200/50 shadow-xl">
                  <ul className="grid w-[280px] gap-1 p-3">
                    {serviceLinks.map((service) => (
                      <li key={service.label}>
                        <NavigationMenuLink asChild>
                          <a
                            href={service.href}
                            className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-securelec-red/10 focus:bg-securelec-red/10 group"
                            data-testid={`services-dropdown-${service.label.toLowerCase()}`}
                          >
                            <div className="text-sm font-semibold text-securelec-gray group-hover:text-securelec-red transition-colors">
                              {service.label}
                            </div>
                            <p className="text-xs text-gray-500 mt-1 leading-snug">
                              {service.description}
                            </p>
                          </a>
                        </NavigationMenuLink>
                      </li>
                    ))}
                    <li className="border-t border-gray-200/50 mt-1 pt-1">
                      <NavigationMenuLink asChild>
                        <a
                          href="/what-we-do"
                          className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-securelec-red/10 focus:bg-securelec-red/10 group text-center"
                          data-testid="services-dropdown-all"
                        >
                          <div className="text-sm font-semibold text-securelec-red group-hover:text-securelec-red/80 transition-colors">
                            View All Services →
                          </div>
                        </a>
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {navLinks.slice(2).map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-securelec-gray hover:text-securelec-red transition-colors duration-300 text-sm font-medium tracking-wide uppercase"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${businessInfo.phone.mick}`}
            className="flex items-center gap-2 text-securelec-gray hover:text-securelec-red transition-colors duration-300"
          >
            <Phone className="w-4 h-4" />
            <span className="text-sm font-medium">Call Now</span>
          </a>
          <a href="/get-a-quote">
            <Button
              className="bg-securelec-red hover:bg-securelec-red/90 text-white px-6 py-2 rounded-md font-semibold relative overflow-hidden group"
              data-testid="header-get-a-quote-btn"
            >
              <span className="relative z-10">Get a Quote</span>
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </Button>
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden text-securelec-gray p-2"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white/85 backdrop-blur-xl border-b border-gray-200/50 transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <nav className="container mx-auto px-6 py-6 flex flex-col gap-4">
          {navLinks.slice(0, 2).map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-securelec-gray hover:text-securelec-red transition-colors duration-300 text-lg font-medium py-2"
              data-testid={`mobile-nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
          
          {/* Mobile Services Dropdown */}
          <div className="border-y border-gray-200/50 py-2">
            <button
              onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
              className="flex items-center justify-between w-full text-securelec-gray hover:text-securelec-red transition-colors duration-300 text-lg font-medium py-2"
              data-testid="mobile-services-toggle"
            >
              <span>Services</span>
              <ChevronDown 
                className={`w-5 h-5 transition-transform duration-300 ${isMobileServicesOpen ? 'rotate-180' : ''}`} 
              />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${isMobileServicesOpen ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="pl-4 pt-2 flex flex-col gap-2">
                {serviceLinks.map((service) => (
                  <a
                    key={service.label}
                    href={service.href}
                    className="text-securelec-gray hover:text-securelec-red transition-colors duration-300 text-base font-medium py-2 flex items-center gap-2"
                    data-testid={`mobile-services-${service.label.toLowerCase()}`}
                  >
                    <span className="w-1.5 h-1.5 bg-securelec-red rounded-full"></span>
                    {service.label}
                  </a>
                ))}
                <a
                  href="/what-we-do"
                  className="text-securelec-red hover:text-securelec-red/80 transition-colors duration-300 text-sm font-semibold py-2"
                  data-testid="mobile-services-all"
                >
                  View All Services →
                </a>
              </div>
            </div>
          </div>

          {navLinks.slice(2).map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-securelec-gray hover:text-securelec-red transition-colors duration-300 text-lg font-medium py-2"
              data-testid={`mobile-nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
          
          <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
            <a
              href={`tel:${businessInfo.phone.mick}`}
              className="flex items-center gap-2 text-securelec-gray hover:text-securelec-red transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium">Call Mick: {businessInfo.phone.mick}</span>
            </a>
            <a href="/get-a-quote">
              <Button className="bg-securelec-red hover:bg-securelec-red/90 text-white w-full py-3 rounded-md font-semibold" data-testid="mobile-get-a-quote-btn">
                Get a Quote
              </Button>
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
